import { useParams } from "react-router-dom"
import { fetchRoadWeatherStationInfo } from "../../api/roadWeatherStationsApi"
import { useEffect, useState } from "react"
import { CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell } from 'recharts';
import { useAppSelector } from "../../stateManagement/hooks/redux";
import { useDispatch } from "react-redux";
import { changeWeatherData } from "../../stateManagement/reducers/WeatherSlice";

const { startOfHour, endOfHour, addHours, getHours, setHours, addMilliseconds, format } = require("date-fns");
const { toZonedTime } = require('date-fns-tz');

export default function RoadWeatherStationReview() {
    const dispatch = useDispatch()
    const { weatherStationId }: any = useParams()
    const [weatherData, setWeatherData] = useState<any>()
    const [timeTicks, setTimeTicks] = useState<any>([])
    const storageWeatherData = useAppSelector(state => state.weather.weatherData)
    const sideBarState = useAppSelector(state => state.sidebar.sidebarIsShown)

    async function initializeWeatherStation() {
        let weatherStationInfo
        if (storageWeatherData) {
            weatherStationInfo = storageWeatherData
        } else {
            weatherStationInfo = await fetchRoadWeatherStationInfo(weatherStationId)
            dispatch(changeWeatherData(weatherStationInfo))
        }
        if (weatherStationInfo) {
            let weatherDataArray = []
            for (let i = 0; i < weatherStationInfo?.weatherData?.updated?.length; i++) {
                let weatherDataObject = {}
                Object.entries(weatherStationInfo?.weatherData).map(([key, value]: any) => {
                    if (key !== 'updated') {
                        Object.assign(weatherDataObject, { [key]: value[i] })
                    }
                    else {
                        let time = (toZonedTime(new Date(value[i]), 'Europe/Moscow')).getTime()
                        Object.assign(weatherDataObject, { time: time })
                    }
                })
                Object.assign(weatherDataObject, { height: 1 })
                weatherDataArray.push(weatherDataObject)
            }
            setWeatherData(weatherDataArray)
            const chartDivWidth: any = document.querySelector('.weather-review-div')?.getBoundingClientRect().width;
            configureTicks(weatherStationInfo?.weatherData.updated, chartDivWidth)
        }
    }

    useEffect(() => {
        initializeWeatherStation()
    }, [])

    useEffect(() => {
        const chartDivWidth: any = document.querySelector('.weather-review-div')?.getBoundingClientRect().width;
        if (storageWeatherData) {
            configureTicks(storageWeatherData?.weatherData.updated, chartDivWidth)
        }
    }, [sideBarState])

    function configureXAxisHoursStep(chartDivWidth: number) {
        if (chartDivWidth > 1600) {
            return 2
        }
        if (chartDivWidth > 1200 && chartDivWidth <= 1600) {
            return 4
        }
        if (chartDivWidth <= 1200) {
            return 6
        }
    }

    const baseTicksForStep: any = {
        hoursTicks6: [0, 6, 12, 18],
        hoursTicks4: [0, 4, 8, 12, 16, 20],
        hoursTicks2: [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22]
    }

    function roundToNearestHourForStep(num: number, numbers: number[]): number {
        const roundedNumbers = numbers.map((n) => Math.round(n));
        const closestNumber = roundedNumbers.reduce((closest, current) => {
            const closestDiff = Math.abs(closest - num);
            const currentDiff = Math.abs(current - num);
            return currentDiff < closestDiff ? current : closest;
        }, roundedNumbers[0]);
        return closestNumber;
    }

    function configureTicks(timeArray: any, chartDivWidth: number) {
        let newTimeTicks = []
        let firstDate = toZonedTime(timeArray[0], 'Europe/Moscow')
        let lastDate = toZonedTime(timeArray[timeArray.length - 1], 'Europe/Moscow')
        let firstTimeTick = addMilliseconds(endOfHour(firstDate), 1)
        let xAxisHoursStep = configureXAxisHoursStep(chartDivWidth)
        let baseTicks = baseTicksForStep[`hoursTicks${xAxisHoursStep}`]
        let firstTimeTickHour = getHours(firstTimeTick)
        if (!baseTicks.includes(firstTimeTickHour)) {
            firstTimeTick = setHours(firstTimeTick, roundToNearestHourForStep(firstTimeTickHour, baseTicks))
        }
        let currentTick = firstTimeTick
        while (currentTick <= addHours(startOfHour(lastDate), xAxisHoursStep)) {
            if (currentTick >= firstDate) {
                newTimeTicks.push(currentTick)
            }
            currentTick = addHours(currentTick, xAxisHoursStep)
        }
        setTimeTicks(newTimeTicks)
    }

    function formatTime(timeTick: any) {
        return format(timeTick, 'HH:mm');
    }

    function formatDate(timeTick: any) {
        return format(timeTick, 'dd.MM.yy');
    }

    const windDirectionOptions: any = {
        0: "",
        1: String.fromCharCode(8593),
        2: String.fromCharCode(8599),
        3: String.fromCharCode(8594),
        4: String.fromCharCode(8600),
        5: String.fromCharCode(8595),
        6: String.fromCharCode(8601),
        7: String.fromCharCode(8592),
        8: String.fromCharCode(8598)
    }

    const roadSurfaceOptions: any = {
        0: {
            name: "нет данных",
            color: "#00000"
        },
        1: {
            name: "лед/снег",
            color: "#55d9fc"
        },
        3: {
            name: "химикаты",
            color: "#75da1b"
        },
        7: {
            name: "мокро",
            color: "#505050"
        },
        9: {
            name: "сухо",
            color: "#7f7f80"
        }
    }

    const weatherOptions: any = {
        0: {
            name: "нет осадков",
            color: "#affb9f"
        },
        1: {
            name: "ясно",
            color: "#96d8f8"
        },
        2: {
            name: "облачно",
            color: "#86a4ad"
        },
        3: {
            name: "дождь",
            color: "#787d98"
        },
        6: {
            name: "снег с дождем",
            color: "#5a5868"
        },
        9: {
            name: "снег",
            color: "#939393"
        }
    }

    function getCustomTooltipAreaChart({ active, payload, label }: any) {
        if (active && payload && payload.length) {
            let dataKey = payload[0]?.dataKey
            return (
                <div className="weather-review-tooltip">
                    {dataKey !== 'ws'
                        ?
                        <p className="value">{payload[0].value}{payload[0].unit}</p>
                        :
                        <p className="value">{payload[0].value}{payload[0].unit}<span className="wind-direction"> {windDirectionOptions[payload[1]?.value]}</span></p>
                    }
                    <div className="date-time-div">
                        <p className="date">{formatDate(label)},</p>
                        <p className="time">{formatTime(label)}</p>
                    </div>
                </div>
            );
        }
        return null;
    };

    function getCustomTooltipSurfaceChart({ active, payload, label }: any) {
        if (active && payload && payload.length) {
            return (
                <div className="weather-review-tooltip">
                    <p className="value">{roadSurfaceOptions[payload[0].payload.surface].name}</p>
                    <div className="date-time-div">
                        <p className="date">{formatDate(label)},</p>
                        <p className="time">{formatTime(label)}</p>
                    </div>
                </div>
            );
        }
        return null;
    };

    function getCustomTooltipWeatherChart({ active, payload, label }: any) {
        if (active && payload && payload.length) {
            return (
                <div className="weather-review-tooltip">
                    <p className="value">{weatherOptions[payload[0].payload.weather].name}</p>
                    <div className="date-time-div">
                        <p className="date">{formatDate(label)},</p>
                        <p className="time">{formatTime(label)}</p>
                    </div>
                </div>
            );
        }
        return null;
    };

    const CustomTick: any = ({ x, y, payload, index }: any) => {
        if (x && y && payload) {
            return (
                <g transform={`translate(${x},${y})`}>
                    {(formatTime(payload.value) == '00:00' || index === 0)
                        ?
                        <>
                            <text x={0} y={0} dy={24} fontWeight={600}>
                                {formatDate(payload.value)}
                            </text>
                            <text x={0} y={0} dy={12} >
                                {formatTime(payload.value)}
                            </text>
                        </>
                        :
                        <text x={0} y={0} dy={12} >
                            {formatTime(payload.value)}
                        </text>
                    }
                </g>
            )
        }
        return null
    }

    return (
        <div className="weather-review-div">
            {weatherData &&
                <>
                    <p>Температура, °С</p>
                    <ResponsiveContainer width="100%" height={190}>
                        <AreaChart
                            width={500}
                            height={190}
                            data={weatherData}
                            syncId="weatherReview"
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" scale="time" type="number" tick={<CustomTick />} interval={0} ticks={timeTicks} tickFormatter={formatTime} domain={['auto', 'auto']} />
                            <YAxis />
                            <Tooltip content={getCustomTooltipAreaChart} />
                            <Area type="monotone" unit="°С" dataKey="at" stroke="#fc7821" strokeWidth={2} fill="#fc7821" />
                        </AreaChart>
                    </ResponsiveContainer>
                    <p className="time-zone-label">Время московское (UTC+3)</p>
                    <p>Влажность воздуха, %</p>
                    <ResponsiveContainer width="100%" height={190}>
                        <AreaChart
                            width={500}
                            height={190}
                            data={weatherData}
                            syncId="weatherReview"
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" scale="time" type="number" tick={<CustomTick />} interval={0} ticks={timeTicks} tickFormatter={formatTime} domain={['auto', 'auto']} />
                            <YAxis domain={[0, 100]} />
                            <Tooltip content={getCustomTooltipAreaChart} />
                            <Area type="monotone" unit="%" dataKey="rh" stroke="#66aede" strokeWidth={2} fill="#66aede" />
                        </AreaChart>
                    </ResponsiveContainer>
                    <p className="time-zone-label">Время московское (UTC+3)</p>
                    <p>Скорость и направление ветра, м/с</p>
                    <ResponsiveContainer width="100%" height={190}>
                        <AreaChart
                            width={500}
                            height={190}
                            data={weatherData}
                            syncId="weatherReview"
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" scale="time" type="number" tick={<CustomTick />} interval={0} ticks={timeTicks} tickFormatter={formatTime} domain={['auto', 'auto']} />
                            <YAxis type="number" allowDataOverflow={true} domain={[0, Math.round(Math.max(...storageWeatherData?.weatherData?.ws) + 1)]} />
                            <Tooltip content={getCustomTooltipAreaChart} />
                            <Area type="monotone" unit=" м/с" dataKey="ws" stroke="#667" strokeWidth={2} fill="#667" />
                            <Area type="monotone" dataKey="wd" stroke="none" fill="none" activeDot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                    <p className="time-zone-label">Время московское (UTC+3)</p>
                    <p>Поверхность дороги</p>
                    <ResponsiveContainer width="100%" height={80}>
                        <BarChart width={500} height={40} data={weatherData} syncId="weatherReview" barGap={"0%"} barSize={30} barCategoryGap={"0%"}>
                            <Tooltip content={getCustomTooltipSurfaceChart} />
                            <XAxis dataKey="time" scale="time" type="number" tick={<CustomTick />} interval={0} ticks={timeTicks} tickFormatter={formatTime} domain={['auto', 'auto']} />
                            <YAxis type="number" allowDataOverflow={true} tick={false} domain={[0, 1]} />
                            <Bar dataKey="height" width={0}>
                                {weatherData.map((entry: any, index: any) => (
                                    <Cell width={300} fill={roadSurfaceOptions[entry.surface].color} key={`cell-${index}`} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <p className="time-zone-label">Время московское (UTC+3)</p>
                    <p>Погода в целом</p>
                    <ResponsiveContainer width="100%" height={80}>
                        <BarChart width={150} height={40} data={weatherData} syncId="weatherReview" barGap={"0%"} barSize={30} barCategoryGap={"0%"}>
                            <Tooltip content={getCustomTooltipWeatherChart} />
                            <XAxis dataKey="time" scale="time" type="number" tick={<CustomTick />} interval={0} ticks={timeTicks} tickFormatter={formatTime} domain={['auto', 'auto']} />
                            <YAxis type="number" allowDataOverflow={true} tick={false} domain={[0, 1]} />
                            <Bar dataKey="height" width={0}>
                                {weatherData.map((entry: any, index: any) => (
                                    <Cell width={300} fill={weatherOptions[entry.weather].color} key={`cell-${index}`} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <p className="time-zone-label">Время московское (UTC+3)</p>
                </>
            }
        </div>
    )
}
